/**
 * Collision detection for sibling-only tree reorder.
 * Uses real droppable rects + pointer Y (not layout-projected sortable rects).
 */
export function siblingPointerCollision({
  active,
  droppableContainers,
  droppableRects,
  pointerCoordinates,
}) {
  if (!active || !pointerCoordinates) {
    return [];
  }

  const siblingIds = active.data?.current?.siblingIds;
  if (!Array.isArray(siblingIds) || siblingIds.length === 0) {
    return [];
  }

  const siblingSet = new Set(siblingIds.map(String));
  const activeId = String(active.id);
  const candidates = droppableContainers.filter(
    (container) => siblingSet.has(String(container.id)) && String(container.id) !== activeId
  );

  // Prefer the sibling whose vertical span contains the pointer
  const containing = [];
  for (const container of candidates) {
    const rect = droppableRects.get(container.id);
    if (!rect) continue;
    if (
      pointerCoordinates.y >= rect.top
      && pointerCoordinates.y <= rect.bottom
    ) {
      containing.push({
        id: container.id,
        data: {
          droppableContainer: container,
          value: Math.abs(pointerCoordinates.y - (rect.top + rect.height / 2)),
        },
      });
    }
  }

  if (containing.length > 0) {
    containing.sort((a, b) => a.data.value - b.data.value);
    return containing;
  }

  // Otherwise pick closest sibling by vertical distance to row center
  let nearest = null;
  let nearestDist = Infinity;
  for (const container of candidates) {
    const rect = droppableRects.get(container.id);
    if (!rect) continue;
    const midY = rect.top + rect.height / 2;
    const dist = Math.abs(pointerCoordinates.y - midY);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = container;
    }
  }

  if (!nearest) {
    return [];
  }

  return [{
    id: nearest.id,
    data: {
      droppableContainer: nearest,
      value: nearestDist,
    },
  }];
}
